/**
 * HtmlFetcherService.js
 * Shared service for fetching and caching HTML content
 */

class HtmlFetcherService {
  constructor() {
    this.htmlCache = new Map();
    
    // Get API URL from environment or fallback to default
    const apiPort = process.env.REACT_APP_API_PORT || '5002';
    this.proxyUrl = process.env.REACT_APP_API_URL || `http://localhost:${apiPort}/api`;
  }

  /**
   * Fetch HTML content for a URL
   * @param {string} url - The URL to fetch HTML from
   * @returns {Promise<string>} - The HTML content
   */
  async fetchHtml(url) {
    try {
      // Check cache first
      if (this.htmlCache.has(url)) {
        console.log(`[HtmlFetcherService] Using cached HTML for ${url}`);
        return this.htmlCache.get(url);
      }

      console.log(`[HtmlFetcherService] Fetching HTML for ${url}`);
      
      // Use the server-side proxy to avoid CORS issues
      const encodedUrl = encodeURIComponent(url);
      const response = await fetch(`${this.proxyUrl}/pagespeed/fetch-html?url=${encodedUrl}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data || !data.html) {
        throw new Error('No HTML content in response');
      }
      
      console.log(`[HtmlFetcherService] Successfully fetched HTML (${data.html.length} bytes)`);
      
      // Cache the HTML content
      this.htmlCache.set(url, data.html);
      
      return data.html;
    } catch (error) {
      console.error(`[HtmlFetcherService] Error fetching HTML: ${error.message}`);
      throw error;
    }
  }

  /**
   * Clear the HTML cache
   */
  clearCache() {
    this.htmlCache.clear();
  }
}

// Create a singleton instance
const htmlFetcherService = new HtmlFetcherService();
export default htmlFetcherService; 